import type { WorkItem } from '@/components/UniversalWorkItems';

interface AssignmentRecord {
  user_id?: unknown;
  userId?: unknown;
  id?: unknown;
  value?: unknown;
  member_id?: unknown;
  employee_id?: unknown;
}

type AssignmentAwareWorkItem = WorkItem & {
  assigned_users?: unknown;
  assigned_users_details?: unknown;
  custom_fields?: Record<string, unknown> | null;
};

const ASSIGNMENT_USER_ID_KEYS = ['user_id', 'userId', 'id', 'value', 'member_id', 'employee_id'] as const;

export function normalizeAssignedUserId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? value : null;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
}

export function parseAssignmentSourceArray(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('[')) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

export function getAssignmentUserIdFromValue(value: unknown): number | null {
  const directId = normalizeAssignedUserId(value);
  if (directId != null) {
    return directId;
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const record = value as AssignmentRecord;

  for (const key of ASSIGNMENT_USER_ID_KEYS) {
    const candidateId = normalizeAssignedUserId(record[key]);
    if (candidateId != null) {
      return candidateId;
    }
  }

  return null;
}

export function getWorkItemAssignedUserIds(item: WorkItem): number[] {
  const assignmentAwareItem = item as AssignmentAwareWorkItem;
  const customFields = assignmentAwareItem.custom_fields && typeof assignmentAwareItem.custom_fields === 'object'
    ? assignmentAwareItem.custom_fields
    : {};
  const assignedUserIds = new Set<number>();

  if (Array.isArray(item.assignees)) {
    item.assignees.forEach((assignee) => {
      const assigneeId = normalizeAssignedUserId(assignee?.user_id);
      if (assigneeId != null) {
        assignedUserIds.add(assigneeId);
      }
    });
  } else {
    [
      assignmentAwareItem.assigned_users,
      assignmentAwareItem.assigned_users_details,
      customFields.assigned_users,
      customFields.assigned_users_details,
      customFields.assigned_user_ids,
      customFields.participant_user_ids,
    ].forEach((source) => {
      parseAssignmentSourceArray(source).forEach((entry) => {
        const entryUserId = getAssignmentUserIdFromValue(entry);
        if (entryUserId != null) {
          assignedUserIds.add(entryUserId);
        }
      });
    });
  }

  const assignedToUserId = normalizeAssignedUserId(item.assigned_to);
  if (assignedToUserId != null) {
    assignedUserIds.add(assignedToUserId);
  }

  return Array.from(assignedUserIds);
}

export function getPrimaryAssignedUserId(item: WorkItem, assignedUserIds: number[] = getWorkItemAssignedUserIds(item)): number | null {
  const assignedToUserId = normalizeAssignedUserId(item.assigned_to);
  if (assignedToUserId != null && assignedUserIds.includes(assignedToUserId)) {
    return assignedToUserId;
  }

  return assignedUserIds[0] ?? null;
}
